import React, { FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Section } from 'src/components/widgets/section';
import { NoIntegrationDialog } from 'src/shared/dialogs';
import { cartesianProduct, T } from 'src/shared/helpers';
import ScratchIcon from 'src/shared/material_icons/editor/draw.svg';
import ImportIcon from 'src/shared/material_icons/file/cloud_download.svg';
import styled from 'styled-components';
import { Link, PrimaryButton } from '../../../../../widgets/buttons';
import { WizardFooterBackButton } from '../../wizardFooterBackButton';
import { useDialogManager } from '../../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { ContainerBase } from '../../../../../widgets/layouts';
import { showNotification } from '../../../../../widgets/notificationToaster';
import { SelectionType, SelectionTypeContainer } from '../../../../../widgets/selectionType';
import { ContainerSmall } from '../../../containers';
import { addNewColorAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import ConnectModelDialog, { PartialSalesChannel } from '../../6-done/connectModelDialog';
import { menuSourceVariantsQuery } from './__generated__/menuSourceVariantsQuery.graphql';
import Stack from 'src/components/widgets/stack/stack';

interface MenuSourceVariantsProps {
    onChooseImport: () => void;
    onChooseScratch: () => void;
    productId: string;
}

const MenuSourceVariantsSection = styled(Section)`
    height: calc(100vh - 150px);
    width: 100%;
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
    > div {
        align-items: center;
        justify-content: center;
    }
`;

const MenuSourceVariantContainerSmall = styled(ContainerSmall)`
    width: 720px;
    max-width: 100%;
    margin: unset;
`;

const GuideLink = styled.div`
    text-align: center;
    margin-bottom: 20px;
    font-size: 14px;

    p {
        margin: 0;
    }
`;

const LearnMoreRow = styled.div`
    text-align: center;
    margin-top: 20px;
    font-size: 14px;
`;

const MenuSourceVariants: FC<MenuSourceVariantsProps> = ({ onChooseImport, onChooseScratch, productId }) => {
    const { state, dispatch } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { showDialog, closeDialog } = useDialogManager();
    const { salesChannels } = useLazyLoadQuery<menuSourceVariantsQuery>(
        graphql`
            query menuSourceVariantsQuery {
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                    }
                }
            }
        `,
        {}
    );

    const onImportVariants = () => {
        if (salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConnectModelDialog
                    additionalCodes={[]}
                    merchantProductOptionsConnected={[]}
                    productColors={[]}
                    productID={''}
                    forImportingVariants={true}
                    salesChannels={realSalesChannels as PartialSalesChannel[]}
                    productSalesChannelID={state.salesChannelID}
                    onConfirmImporting={(sourceMerchantProductOptions) => {
                        if (sourceMerchantProductOptions.filter((opt) => opt.optionID && opt.optionID != '').length > 0) {
                            let variantsImported = sourceMerchantProductOptions.map((attr) =>
                                attr.values.map((opt) => {
                                    return { Id: attr.optionID, Label: attr.label, Value: { Id: opt.valueID, Label: opt.label } };
                                })
                            );
                            let variantsToSave =
                                sourceMerchantProductOptions.length == 1 ? variantsImported[0].map((v) => [v]) : cartesianProduct(...variantsImported);
                            variantsToSave.forEach((variant: SelectedOptionValue[]) => {
                                dispatch(addNewColorAction(variant));
                            });

                            if (onChooseImport) onChooseImport();
                        } else {
                            showNotification({
                                type: 'danger',
                                message: <>{T._('Unable to import: product without variants.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        closeDialog('connectProductDialog');
                    }}
                />
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    titleNoIntegration={T._('Connect a store to import variants', 'Admin')}
                    subTitleNoIntegration={T._('Importing variants requires a connected online store.', 'Admin')}
                    message={
                        <label>
                            {T._(
                                'To import variants automatically, Zakeke reads them from a product in your store. Connect your store and link this product to bring in its variants — or go back and create them from scratch.',
                                'Admin'
                            )}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }
    };

    return (
        <ContainerBase>
            <Stack>
                <MenuSourceVariantsSection>
                    <MenuSourceVariantContainerSmall isFullWidth={true}>
                        <GuideLink>
                            <p>{T._('Choose how you want to define the product variants:', 'Admin')}</p>
                            <p>{T._('create them manually from scratch or import them from your store.', 'Admin')}</p>
                        </GuideLink>
                        <SelectionTypeContainer>
                            <SelectionType
                                key='scratch'
                                label={T._('Scratch', 'Admin')}
                                icon={<ScratchIcon />}
                                iconSize={36}
                                iconColor='#7C4DFF'
                                iconBackground='#F0EBFF'
                                onClick={onChooseScratch}
                                tip={<>{T._('Build your variants by hand: define attributes like size and color and their options yourself.', 'Admin')}</>}
                            />
                            <SelectionType
                                key='import'
                                label={T._('Import', 'Admin')}
                                icon={<ImportIcon />}
                                iconSize={36}
                                iconColor='#00B894'
                                iconBackground='#E4F8F1'
                                onClick={onImportVariants}
                                tip={
                                    <>
                                        {T._(
                                            'Connect Zakeke to an online store and link the product you are building to a product on that site to import the same structure. That product will become a Zakeke-customizable product on your store.',
                                            'Admin'
                                        )}
                                    </>
                                }
                            />
                        </SelectionTypeContainer>
                        <LearnMoreRow>
                            <Link
                                target='_blank'
                                href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants-Detailed-guide-on-setting-them-up-for-your-customizable-product#h_01K3NB1SE7BYZKC9HBDNVQ5D1V'
                            >
                                {T._('Learn more', 'Admin')}
                            </Link>
                        </LearnMoreRow>
                    </MenuSourceVariantContainerSmall>
                </MenuSourceVariantsSection>
                <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={() => changeStep('print-area')} />}>
                    <PrimaryButton $size='large' onClick={() => changeStep('preview')}>
                        {T._(`I don't offer variants, go to review`, 'Admin')}
                    </PrimaryButton>
                </PageFooterButtons>
            </Stack>
        </ContainerBase>
    );
};

export default MenuSourceVariants;
